/**
 * GridEventCardSwitch for CalendarGrid
 * Routes CalendarGrid events to appropriate event card variants and tooltips.
 */
import React from 'react';
import { EventStatus } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { CalendarEvent } from '@frontend/calendar-grid';
import { ScheduleRequestEventData } from '@frontend/schedule-helpers';
import { OverlappingEventIndicatorVariantType } from '../calendar-view-v3/overlapping-event-indicator';
import { GridAppointmentCard, GridAppointmentTooltip } from './variants-grid/grid-appointment-card';
import { GridExceptionCard, GridExceptionTooltip } from './variants-grid/grid-exception-card';
import { GridScheduleRequestCard, GridScheduleRequestTooltip } from './variants-grid/grid-schedule-request-card';

dayjs.extend(customParseFormat);

/**
 * Grid calendar event type that extends the base CalendarEvent with
 * additional data needed for rendering event cards in the schedule feature.
 *
 * Includes all required properties from CalendarEvent (id, startTime, duration, eventType)
 * plus enriched data from the API response (patient info, location, status, etc.)
 */
export type GridCalendarEvent = CalendarEvent & {
  // Enriched data from original event
  title?: string;
  startDate?: string;
  /** Start time in hh:mm A format — used for drag-drop duration calculation */
  startHour?: string;
  endDate?: string;
  /** End time in hh:mm A format — used for drag-drop duration calculation */
  endHour?: string;
  locationName?: string;
  locationId?: string;
  entityId?: string;
  entityName?: string;
  personStatus?: string;
  eventStatus?: EventStatus;
  eventTypeColor?: string;
  backgroundColor?: string;
  calendarDate?: string;
  overlappingIndicatorVariant?: OverlappingEventIndicatorVariantType;
  isYesterdayEvent?: boolean;
  scheduleRequestEventData?: ScheduleRequestEventData;
  // Drag-drop enriched fields (appointments only)
  /** Patient / attendee ID */
  patientId?: string;
  /** Appointment reference ID (for activity history invalidation) */
  referenceId?: string;
  /** Appointment type ID (referenceTypeId) — used for provider mapping checks */
  appointmentTypeId?: string;
  /** Appointment type name */
  appointmentTypeName?: string;
  /** Provider or operatory calendar ID — used as organizerCalendarId in update mutation */
  calendarId?: string;
  /** Display options mirroring EventData.displayOptions */
  displayOptions?: {
    primaryColor?: string;
    backgroundColor?: string;
    textColor?: string;
  };
};

type GridEventCardSwitchProps = {
  event: GridCalendarEvent;
};

//#region Card

export const GridEventCardSwitch = ({ event }: GridEventCardSwitchProps) => {
  const { startHour, endHour } = deriveHourStrings(event);

  // Extract common props
  const commonProps = {
    id: event.id,
    title: event.title ?? '',
    startDate: event.startDate ?? '',
    startHour,
    endHour,
    locationName: event.locationName,
    locationId: event.locationId,
    entityId: event.entityId,
    entityName: event.entityName,
    eventTypeColor: event.eventTypeColor,
    backgroundColor: event.backgroundColor,
    calendarDate: event.calendarDate,
  };

  switch (event.eventType) {
    case 'appointment':
      return (
        <GridAppointmentCard
          {...commonProps}
          personStatus={event.personStatus}
          eventStatus={event.eventStatus}
          overlappingIndicatorVariant={event.overlappingIndicatorVariant}
          isYesterdayEvent={event.isYesterdayEvent}
          scheduleRequestEventData={event.scheduleRequestEventData}
        />
      );
    case 'scheduleRequest':
      return (
        <GridScheduleRequestCard
          {...commonProps}
          personStatus={event.personStatus}
          overlappingIndicatorVariant={event.overlappingIndicatorVariant}
          isYesterdayEvent={event.isYesterdayEvent}
          scheduleRequestEventData={event.scheduleRequestEventData}
        />
      );
    case 'exception':
    case 'unavailableBlock':
      return (
        <GridExceptionCard
          {...commonProps}
          title={event.title || 'Out of Office'}
          eventType={event.eventType === 'exception' ? 'exception' : 'break'}
        />
      );
    default:
      // For other event types, render a default exception card
      return <GridExceptionCard {...commonProps} title={event.title || event.eventType} />;
  }
};

//#endregion

//#region Tooltip

/**
 * Render function for CalendarGrid's `renderEventTooltip` prop.
 *
 * Dispatches to the per-type tooltip component that lives alongside each card
 * variant, keeping tooltip content co-located with its card design:
 *   - appointment      → GridAppointmentTooltip  (grid-appointment-card.tsx)
 *   - scheduleRequest  → GridScheduleRequestTooltip (grid-schedule-request-card.tsx)
 *   - exception / unavailableBlock → GridExceptionTooltip (grid-exception-card.tsx)
 *   - other types      → Exception tooltip with title fallback
 *
 * Usage:
 *   <CalendarGrid renderEventTooltip={renderGridEventTooltip} ... />
 */
export const GridEventTooltipSwitch = ({ event }: { event: GridCalendarEvent }): React.ReactNode => {
  const { startHour, endHour } = deriveHourStrings(event);

  switch (event.eventType) {
    case 'appointment':
      return (
        <GridAppointmentTooltip
          title={event.title ?? ''}
          startHour={startHour}
          endHour={endHour}
          appointmentType={event.appointmentTypeName}
        />
      );
    case 'scheduleRequest':
      return (
        <GridScheduleRequestTooltip
          startHour={startHour}
          endHour={endHour}
          scheduleRequestEventData={event.scheduleRequestEventData}
          appointmentType={event.appointmentTypeName}
        />
      );
    case 'exception':
    case 'unavailableBlock':
      return (
        <GridExceptionTooltip
          title={event.title || (event.eventType === 'exception' ? 'Out of Office' : 'Break')}
          startHour={startHour}
          endHour={endHour}
        />
      );
    default:
      return <GridExceptionTooltip title={event.title ?? ''} startHour={startHour} endHour={endHour} />;
  }
};

//#endregion

/**
 * Derives display-ready start/end hour strings in "h:mm A" format from a GridCalendarEvent.
 *
 * Preference order:
 *   1. Use the enriched `startHour` / `endHour` when already present (populated by the data hook).
 *   2. Fall back to deriving from `startTime` ("HH:mm") + `duration` (minutes) from the base
 *      CalendarEvent — converting to 12-hour "h:mm A" format that card/tooltip consumers expect.
 */
const deriveHourStrings = (event: GridCalendarEvent): { startHour: string; endHour: string } => {
  if (event.startHour && event.endHour) {
    return { startHour: event.startHour, endHour: event.endHour };
  }

  // Parse the base CalendarEvent's "HH:mm" startTime into a dayjs object
  const startDayjs = dayjs(event.startTime, 'HH:mm');
  const startHour = event.startHour ?? (startDayjs.isValid() ? startDayjs.format('h:mm A') : '');

  let endHour = event.endHour ?? '';
  if (!endHour) {
    if (event.duration != null && startDayjs.isValid()) {
      endHour = startDayjs.add(event.duration, 'minute').format('h:mm A');
    } else {
      // Last resort: no duration available — use startHour so the display is at
      // least not a raw 24-hour string, even though duration will show as zero.
      endHour = startHour;
    }
  }

  return { startHour, endHour };
};
